import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { BatchImportDialog } from "./BatchImportDialog";
import { BatchImportProgressToast } from "./BatchImportProgressToast";
import { BatchImportType } from "./batchImportConfig";
import { useBatchImport } from "./useBatchImport";
import { ImageLinkImportDialog } from "./link/ImageLinkImportDialog";
import { useImageLinkImport } from "./link/useImageLinkImport";
import { ImportTable } from "@/lib/import/import-types";

/**
 * Zone "chevron" d'un split-button : ouvre un menu avec l'action d'import batch
 * d'images, puis la modale dédiée. L'état d'import vit ici (le menu reste monté
 * sur le dashboard) pour survivre à la fermeture de la modale et alimenter la
 * notification de progression bottom-right.
 */
export function BatchImportMenu({
  type,
  disabled,
  triggerClassName,
}: {
  type: BatchImportType;
  disabled?: boolean;
  triggerClassName?: string;
}) {
  const vt = useVisibleT();
  const [open, setOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const batch = useBatchImport(type);
  // L'etat vit ici, comme celui de `batch` : le menu reste monte sur le
  // dashboard, donc fermer la modale ne tue pas le rattachement en cours.
  const link = useImageLinkImport(type as ImportTable);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            className={cn("px-2", triggerClassName)}
            disabled={disabled}
            aria-label={vt("bo.batchImport.more", "Plus d'options d'ajout")}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setOpen(true)}>
            {vt("bo.batchImport.menuItem", "Importer des images en lot")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setLinkOpen(true)}>
            {vt(
              "bo.imageLink.menuItem",
              "Lier des images à des fiches existantes",
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <BatchImportDialog
        type={type}
        open={open}
        onOpenChange={setOpen}
        batch={batch}
      />
      <BatchImportProgressToast
        batch={batch}
        open={open}
        onOpen={() => setOpen(true)}
      />
      <ImageLinkImportDialog
        table={type as ImportTable}
        open={linkOpen}
        onOpenChange={setLinkOpen}
        link={link}
      />
    </>
  );
}

export default BatchImportMenu;
